/**
 * Mobile OTP authentication client for rivyoo-server.
 * Strictly aligned with mobile app endpoints in `packages/api/auth.ts`:
 * - POST /auth/register-otp/
 * - POST /auth/send-otp/
 * - POST /auth/verify-otp/
 * - GET  /auth/user/
 * - POST /auth/logout/
 */

import { getApiBaseUrl } from "./config";
export { getApiBaseUrl };

/**
 * Sends OTP to the provided 10-digit mobile number.
 * Tries /auth/register-otp/ first; if user already exists (409 or needsSendOtp),
 * falls back to /auth/send-otp/.
 */
export async function requestMobileOtp(mobileNumber) {
  const digits = String(mobileNumber).replace(/\D/g, "");
  if (digits.length !== 10) {
    throw new Error("Please enter a valid 10-digit mobile number");
  }

  const baseUrl = getApiBaseUrl();
  const headers = { "Content-Type": "application/json" };
  const body = JSON.stringify({ phone_number: digits });

  // 1. Attempt register-otp
  try {
    const res = await fetch(`${baseUrl}/auth/register-otp/`, {
      method: "POST",
      headers,
      body,
    });

    const data = await res.json().catch(() => ({}));

    // 409 conflict: user already registered, send OTP directly
    if (res.status === 409 || data.needsSendOtp) {
      const sendRes = await fetch(`${baseUrl}/auth/send-otp/`, {
        method: "POST",
        headers,
        body,
      });
      const sendData = await sendRes.json().catch(() => ({}));
      if (!sendRes.ok) {
        throw new Error(sendData.error || sendData.detail || "Failed to send OTP. Please try again.");
      }
      return { success: true, isExistingUser: true, ...sendData };
    }

    if (!res.ok) {
      // Check if message says already exists
      const msg = (data.error || data.detail || "").toLowerCase();
      if (msg.includes("already exists") || res.status === 400) {
        const sendRes = await fetch(`${baseUrl}/auth/send-otp/`, {
          method: "POST",
          headers,
          body,
        });
        const sendData = await sendRes.json().catch(() => ({}));
        if (sendRes.ok) {
          return { success: true, isExistingUser: true, ...sendData };
        }
      }
      throw new Error(data.error || data.detail || "Failed to send verification code.");
    }

    return { success: true, isExistingUser: false, ...data };
  } catch (err) {
    if (err instanceof Error) throw err;
    throw new Error("Network error while requesting OTP. Please check your connection.");
  }
}

/**
 * Verifies the 6-digit OTP code against the mobile number.
 * Returns { token, refreshToken, user }.
 */
export async function verifyMobileOtp(mobileNumber, otpCode) {
  const digits = String(mobileNumber).replace(/\D/g, "");
  const otp = String(otpCode).trim();

  if (digits.length !== 10) {
    throw new Error("Invalid mobile number");
  }
  if (!/^\d{6}$/.test(otp)) {
    throw new Error("Please enter a valid 6-digit OTP");
  }

  const baseUrl = getApiBaseUrl();
  const res = await fetch(`${baseUrl}/auth/verify-otp/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      phone_number: digits,
      otp_code: otp,
    }),
  });

  const data = await res.json().catch(() => ({}));

  if (!res.ok) {
    const errorMsg =
      data.error || data.details?.otp_code?.[0] || data.detail || "Invalid verification code. Please try again.";
    throw new Error(errorMsg);
  }

  // Extract access token and profile
  const token = data.access_token || data.token;
  const refreshToken = data.refresh_token || data.refreshToken;
  const user = data.user || {
    id: data.id,
    phone_number: digits,
    name: data.name || `Member (${digits.slice(-4)})`,
  };

  return {
    token,
    refreshToken,
    user,
  };
}

/**
 * Fetches the authenticated user profile via /auth/user/.
 */
export async function fetchCurrentUser(token) {
  const baseUrl = getApiBaseUrl();
  const res = await fetch(`${baseUrl}/auth/user/`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!res.ok) {
    throw new Error(`Failed to fetch user profile (${res.status})`);
  }

  return res.json();
}

/**
 * Best-effort logout call to the backend.
 */
export async function logoutUser(token) {
  if (!token) return;
  try {
    const baseUrl = getApiBaseUrl();
    await fetch(`${baseUrl}/auth/logout/`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });
  } catch {
    // Ignore network error on logout
  }
}

/**
 * Checks onboarding status via /onboarding/progress/.
 */
export async function fetchOnboardingProgress(token) {
  const baseUrl = getApiBaseUrl();
  const res = await fetch(`${baseUrl}/onboarding/progress/`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!res.ok) {
    return { is_onboarded: false };
  }

  return res.json();
}

/**
 * Completes user profile via POST /onboarding/complete-profile/.
 * Payload matches mobile app: { name, referral_code, email }
 */
export async function submitCompleteProfile(token, { name, email, referralCode }) {
  const baseUrl = getApiBaseUrl();
  const payload = {
    name: String(name).trim(),
    referral_code: (referralCode && String(referralCode).trim()) || "BETA2026",
    ...(email && String(email).trim() ? { email: String(email).trim() } : {}),
  };

  const res = await fetch(`${baseUrl}/onboarding/complete-profile/`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });

  const data = await res.json().catch(() => ({}));
  if (!res.ok || data.success === false) {
    throw new Error(data.error || data.message || "Failed to complete profile");
  }

  return data;
}

/**
 * Saves preferred category IDs via POST /onboarding/category-prefs/.
 */
export async function submitCategoryPreferences(token, categoryIds = []) {
  const baseUrl = getApiBaseUrl();
  const res = await fetch(`${baseUrl}/onboarding/category-prefs/`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ category_ids: categoryIds }),
  });

  const data = await res.json().catch(() => ({}));
  if (!res.ok || data.success === false) {
    throw new Error(data.error || data.message || "Failed to save category preferences");
  }

  return data;
}
